import { useEffect, useState } from 'react'
import { api, ApiError, can, type Place, type SiteNote } from '../lib/api'

/** What somebody should know about this address before they arrive.
 *
 *  Section 06 calls these the exception to the rule that history must not
 *  interrupt: "entry instructions, access codes, where the gate is, a dog on
 *  the property, hazardous equipment on site. Anything a responder benefits
 *  from knowing before arriving rather than discovering on the doorstep."
 *
 *  SO THEY DO NOT SIT IN A LIST.
 *
 *  Address history is glanceable and dismissible. These are not. They sit at
 *  the top of the call being worked, in full, unread-proof — because the
 *  failure mode is a responder finding the dog himself.
 *
 *  EVERY NOTE SHOWS ITS DATE AND ITS AUTHOR.
 *
 *  The plan is honest that add-only notes eventually contradict each other —
 *  the dog dies, the gate code changes, and the old note stays. Dates and
 *  authors are the stated mitigation rather than a cure, so they are never
 *  optional here: a responder has to be able to see that a note is three years
 *  old and weigh it.
 */

const SEVERITY: Record<string, string> = {
  danger: 'Danger',
  caution: 'Caution',
  info: 'Note',
}

function whenWritten(iso: string): string {
  const d = new Date(iso)
  const months = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 30)
  const stamp = d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
  // Said in words when it is old enough to matter. "Mar 2023" is a date a
  // dispatcher reads past; "2 years ago" is one he weighs.
  if (months >= 12) return `${stamp} — ${Math.floor(months / 12)}y ago`
  if (months >= 3) return `${stamp} — ${Math.floor(months)} months ago`
  return stamp
}

/** The entry code and cross streets as typed, beside what they were.
 *
 *  `was` IS WHAT MAKES THE SAVE SAFE. `PATCH /api/places/{id}` treats every
 *  key it is handed as an instruction to write that column, so a body has to
 *  carry only what somebody actually typed over. Two failures come out of
 *  sending the rest: `entry_codes` from a role that may not set one turns a
 *  cross-street correction into a 403, and a field left untouched while its
 *  value changed elsewhere gets overwritten with what was on screen before it
 *  changed.
 *
 *  `placeId` IS ON IT BECAUSE THIS COMPONENT IS NOT REMOUNTED PER CALL.
 *  Dispatch.tsx renders one `<SiteNotes>` and swaps the place under it, so an
 *  open form outlives the address it was opened on — and a gate code typed for
 *  one house, then saved after the dispatcher switched calls, lands on a
 *  different building with nothing on screen to say so.
 */
type SiteDraft = {
  placeId: string
  codes: string
  crossA: string
  crossB: string
  was: { codes: string; crossA: string; crossB: string }
}

export function SiteNotes({
  place, onChanged, onCrossStreets,
}: {
  place: Place | null
  onChanged: (updated: Place) => void
  /** On a working call: the corrected pair is also what THIS call carries.
   *  The address keeps them for the next call; the call froze its own copy
   *  when it opened, and a re-broadcast reads that one. */
  onCrossStreets?: (a: string | null, b: string | null) => Promise<void>
}) {
  const [adding, setAdding] = useState(false)
  const [body, setBody] = useState('')
  const [severity, setSeverity] = useState('info')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [replacing, setReplacing] = useState<SiteNote | null>(null)
  const [draft, setDraft] = useState<SiteDraft | null>(null)

  // THE NOTE COMPOSER CLOSES WHEN THE ADDRESS CHANGES, and it did not.
  // `WorkArea` has no `key`, so switching calls swaps `place` underneath this
  // component without remounting it. The site form is scoped by `placeId` and
  // was safe; `adding` was not, so opening the composer on one call and coming
  // back to another left BOTH forms open at once, sharing one `busy` and one
  // `error`, with the site form's autoFocus stealing the cursor out of the
  // note. `Dispatch.tsx` clears its narrative draft on `call.id` for the same
  // reason and it is the same failure.
  //
  // The half-typed note goes with it. A note carries its author and its date
  // and is a statement about a building; finishing one under a different
  // address is the worse outcome.
  const placeId = place?.id
  useEffect(() => {
    setAdding(false)
    setReplacing(null)
    setBody('')
    setError(null)
  }, [placeId])

  if (!place) return null

  // THE COMPONENT ASKS WHO IS SIGNED IN; IT IS NOT TOLD.
  //
  // This used to take a `canEdit` prop defaulting to true, and its one mount —
  // the working call in Dispatch.tsx — never passed it. So the gate existed in
  // the signature, read as a gate to anybody scanning the file, and granted
  // every role the whole note surface. A prop nobody passes is not a
  // permission check; it is a comment shaped like one, and it is gone.
  //
  // One answer covers all three writes because the server takes the same view:
  // `app/routes/places.py` refuses add, supersede AND retire without
  // `add_site_note`, retire included, precisely so a role cannot pull another
  // dispatcher's note off the list while being unable to write one.
  const mayWrite = can('calls.incident_record.add_site_note')

  // A GATE CODE AND A CROSS STREET ARE NOT THE SAME PERMISSION, and the server
  // is what draws the line rather than this file. `app/routes/places.py`
  // refuses `entry_codes` in the body without this key and asks for nothing
  // beyond a session for the cross streets — its own comment gives the reason: a
  // cross street is part of describing where a call IS and belongs to taking
  // it, while a gate code is what §19 separates out. So the cross-streets box
  // is offered to everybody who can see this panel and the code box only to a
  // role holding the key.
  //
  // IT IS THE READ PERMISSION TOO, AND THAT IS WHY THE BOX IS NOT MERELY
  // DISABLED FOR EVERYBODY ELSE. `places.for_reader` replaces `entry_codes`
  // with null for a role without this key, so an empty value means either "no
  // code recorded" or "not yours to see" and nothing on the wire tells the two
  // apart. Drawing the field only for `mayCodes` is what keeps a blank box
  // unambiguous — that role is shown the real value, so empty means empty —
  // and it is also what stops a save built from a blank box erasing a code the
  // dispatcher was never allowed to see.
  const mayCodes = can('calls.incident_record.entry_codes')

  // AN OPEN FORM BELONGS TO THE ADDRESS IT WAS OPENED ON. `Dispatch.tsx`
  // renders one of these and swaps `place` underneath it, so without the
  // comparison a code typed while one call was selected would save onto
  // whichever building was on screen when Save was pressed.
  //
  // It is one slot, so opening the form on a SECOND address replaces the
  // first's typing. That is the trade taken deliberately: this is a two-field
  // form on the working call, not a record dialog, and a per-place map of
  // half-typed drafts is more machinery than a gate code is worth. Switching
  // away and back finds the typing; opening the form somewhere else does not.
  const editing = draft && draft.placeId === place.id ? draft : null

  const notes = place.notes ?? []
  const worst = notes.some((n) => n.severity === 'danger')
    ? 'danger'
    : notes.some((n) => n.severity === 'caution')
      ? 'caution'
      : 'info'

  async function save() {
    if (!body.trim()) return
    setBusy(true)
    setError(null)
    try {
      const updated = replacing
        ? await api.siteNoteSupersede(place!.id, replacing.id, body.trim(), severity)
        : await api.siteNoteAdd(place!.id, body.trim(), severity)
      onChanged(updated)
      setBody('')
      setSeverity('info')
      setAdding(false)
      setReplacing(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that note.')
    } finally {
      setBusy(false)
    }
  }

  async function retire(note: SiteNote) {
    if (!confirm('Stop showing this note? It stays in the record.')) return
    setBusy(true)
    try {
      onChanged(await api.siteNoteRetire(place!.id, note.id))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not retire that note.')
    } finally {
      setBusy(false)
    }
  }

  function openSite() {
    const was = {
      codes: place!.entry_codes ?? '',
      crossA: place!.cross_street_a ?? '',
      crossB: place!.cross_street_b ?? '',
    }
    setDraft({ placeId: place!.id, ...was, was })
    setError(null)
  }

  async function saveSite() {
    if (!editing) return
    const codes = editing.codes.trim()
    const crossA = editing.crossA.trim()
    const crossB = editing.crossB.trim()

    // ONLY WHAT WAS TYPED OVER GOES IN THE BODY. The `mayCodes` guard is here
    // as well as on the field because this is the half that matters: the
    // route reads the PRESENCE of the key, so a role that cannot set a code
    // sending `entry_codes: null` to say "I did not touch it" is refused 403
    // and loses the cross street it was actually trying to save.
    const patch: {
      entry_codes?: string | null
      cross_street_a?: string | null
      cross_street_b?: string | null
    } = {}
    if (mayCodes && codes !== editing.was.codes) patch.entry_codes = codes || null
    // THE PAIR TRAVELS TOGETHER. "Between Bernard and ___" is not a place, so a
    // change to either sends both, and the route stores what it is handed.
    if (crossA !== editing.was.crossA || crossB !== editing.was.crossB) {
      patch.cross_street_a = crossA || null
      patch.cross_street_b = crossB || null
    }

    // Opened, read, and closed again. The route answers 400 "Nothing to
    // change." to an empty body, and a dispatcher who thought better of it
    // does not need to read an error about having changed nothing.
    if (Object.keys(patch).length === 0) {
      setDraft(null)
      return
    }

    setBusy(true)
    setError(null)
    try {
      onChanged(await api.placeUpdate(place!.id, patch))
      if (onCrossStreets && 'cross_street_a' in patch) {
        await onCrossStreets(patch.cross_street_a ?? null, patch.cross_street_b ?? null)
      }
      setDraft(null)
    } catch (e) {
      // The form stays open with the typing in it, and the sentence lands in
      // the same notice the note writes use. A gate code retyped from memory
      // after a silent failure is a gate code nobody can trust.
      setError(e instanceof ApiError ? e.message : 'Could not save that.')
    } finally {
      setBusy(false)
    }
  }

  const between = [place.cross_street_a, place.cross_street_b].filter(Boolean).join(' and ')
  const nothing = notes.length === 0 && !place.entry_codes && !between

  return (
    <div className={`sitenotes ${notes.length ? worst : 'empty'}`}>
      <div className="sn-title">
        Before anyone goes
        <span className="dim">{place.label}</span>
        {/* THE PLACE TO ADD ONE IS THE PLACE THAT SHOWS IT. Until this button
            existed `PATCH /api/places/{id}` had no caller anywhere in the
            product, so the entry-codes row below could only ever be empty and
            §06's "anything a responder benefits from knowing before arriving"
            had nowhere to be typed. It goes here because here is where the
            caller is on the phone saying "the code is 8891".

            Drawn for every role, because cross streets need nothing beyond a
            session — the form is what narrows to a code field or not.

            The two labels share their tail so the control reads as one thing
            whichever role is looking, and the one for a role without the codes
            key names only cross streets: a button offering to edit a code that
            role cannot see or set is the refused-control defect. */}
        {!adding && !editing && (
          <button className="ghost tiny" style={{ marginLeft: 'auto' }}
                  onClick={openSite}>
            {mayCodes ? 'Codes & cross streets' : 'Cross streets'}
          </button>
        )}
        {/* `button.tiny` carries a `margin-left: auto` of its own, so two of
            them in this flex row split the slack between them and drift apart
            with a gap in the middle. The first absorbs it; `.nogrow` is the
            utility that already exists for pinning the second beside it
            (theme.css), rather than an inline style re-solving it here. */}
        {mayWrite && !adding && !editing && (
          <button
            className="ghost tiny nogrow"
            onClick={() => { setAdding(true); setReplacing(null); setError(null) }}
          >
            Add a note
          </button>
        )}
      </div>

      {error && <div className="notice">{error}</div>}

      {/* Codes and cross streets belong to the building, not to one call. */}
      {place.entry_codes && (
        <div className="sn-note info">
          <span className="sev">Entry</span>
          <span className="body">{place.entry_codes}</span>
        </div>
      )}
      {between && (
        <div className="sn-note info">
          <span className="sev">Between</span>
          <span className="body">{between}</span>
        </div>
      )}

      {notes.map((n) => (
        <div className={`sn-note ${n.severity}`} key={n.id}>
          <span className="sev">{SEVERITY[n.severity] ?? 'Note'}</span>
          <span className="body">{n.body}</span>
          <span className="by">
            {n.author_label}
            {n.written_at ? ` · ${whenWritten(n.written_at)}` : ''}
          </span>
          {/* YOUR OWN NOTE IS YOURS TO CORRECT, SOMEBODY ELSE'S IS NOT.
              `_refuse_if_not_yours` lets the author supersede or retire what he
              wrote and asks `edit_others_note` for everybody else's, so gating
              these two on the permission alone would take Replace and Retire
              off the dispatcher's own notes — the one case the server always
              allows. `mine` is computed server-side from `author_user_id`,
              which never leaves it, so this is the only way the screen can tell
              the two apart.

              Retire is the one that had to be fixed. It asks window.confirm()
              FIRST, so a dispatcher decided a gate code was stale, confirmed
              removing it, and only then met a 403 from a button that could
              never have worked. */}
          {mayWrite && (n.mine || can('calls.incident_record.edit_others_note')) && (
            <>
              <button
                className="ghost tiny"
                disabled={busy}
                onClick={() => {
                  setReplacing(n)
                  setBody(n.body)
                  setSeverity(n.severity)
                  setAdding(true)
                }}
                title="Write a replacement. The old note keeps its date and author."
              >
                Replace
              </button>
              <button className="ghost tiny" disabled={busy}
                      onClick={() => retire(n)}>
                Retire
              </button>
            </>
          )}
        </div>
      ))}

      {nothing && !adding && !editing && (
        <div className="sn-note info">
          {/* THIS SENTENCE CANNOT BE SAID TO A ROLE THAT CANNOT SEE CODES.
              `places.for_reader` hands that role `entry_codes: null` whether
              or not one is recorded, so `nothing` is true above a place that
              has a gate code on it — and "nothing recorded" would be a
              confident claim about the one field on this panel the reader was
              never shown. The narrower sentence accounts only for what that
              role can actually see, and says nothing either way about a code. */}
          <span className="body dim">
            {mayCodes
              ? 'Nothing recorded about this address yet.'
              : 'No notes or cross streets recorded for this address.'}
          </span>
        </div>
      )}

      {editing && (
        <div className="sn-add">
          {mayCodes && (
            <div className="field">
              <label htmlFor="sn-codes">Entry codes</label>
              {/* The placeholder is an EXAMPLE of the shape, never a reading
                  of the empty box. A blank here means no code is recorded —
                  this field is drawn only for the role that is shown the real
                  value — and the box says nothing about that either way. */}
              {/* 400, the same ceiling `PlacePatch` sets. Not decoration: a
                  Pydantic length refusal comes back as a LIST in `detail`,
                  which `request` hands straight to `ApiError`, so the notice
                  under this form would read "[object Object]" — and this is
                  the file whose own catch comment says a gate code retyped
                  from memory after a silent failure is a gate code nobody can
                  trust. */}
              <input
                id="sn-codes"
                autoFocus
                maxLength={400}
                value={editing.codes}
                placeholder="Gate 8891, buzzer 12, key in the lockbox"
                onChange={(e) => setDraft({ ...editing, codes: e.target.value })}
              />
            </div>
          )}
          <div className="field">
            <label htmlFor="sn-cross">Between</label>
            {/* 120 each, `PlacePatch`'s ceiling -- see the codes box above for
                why the browser must hold it too. */}
            <div className="between">
              <input
                id="sn-cross"
                autoFocus={!mayCodes}
                maxLength={120}
                value={editing.crossA}
                placeholder="Bernard"
                onChange={(e) => setDraft({ ...editing, crossA: e.target.value })}
              />
              <span className="dim">and</span>
              <input
                aria-label="and"
                maxLength={120}
                value={editing.crossB}
                placeholder="Van Horne"
                onChange={(e) => setDraft({ ...editing, crossB: e.target.value })}
              />
            </div>
          </div>
          {/* NOT A NOTE, AND IT SAYS SO. A note is superseded and keeps its
              date and author; these two columns are overwritten, because "the
              code is 8891" is a fact about the building now and a stale one is
              a responder standing at a gate. Anything that needs a trail is
              written as a note instead.

              The second sentence is only true for a role holding
              `add_site_note`, since that is what draws the button it sends the
              dispatcher to — advice pointing at a control that is not on the
              screen reads as a broken screen. */}
          <p className="footnote">
            These belong to the address and are rewritten in place — no date and
            no author.
            {mayWrite && ' Write a note instead for anything that should keep both.'}
          </p>
          <div className="btnrow">
            <button className="primary compact" disabled={busy} onClick={saveSite}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button className="ghost" disabled={busy}
                    onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {adding && (
        <div className="sn-add">
          {replacing && (
            <p className="footnote">
              Replacing {replacing.author_label}'s note. The old one keeps its
              date and author and stops being shown — nothing is rewritten.
            </p>
          )}
          <textarea
            autoFocus
            rows={2}
            value={body}
            placeholder="Gate code 4402. Dog in the back yard. Lift is out."
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="btnrow">
            {(['info', 'caution', 'danger'] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={`chip ${severity === s ? 'on' : ''} ${s}`}
                onClick={() => setSeverity(s)}
              >
                {SEVERITY[s]}
              </button>
            ))}
            <button className="primary compact" disabled={busy || !body.trim()}
                    onClick={save}>
              {busy ? 'Saving…' : replacing ? 'Replace it' : 'Add it'}
            </button>
            <button
              className="ghost"
              onClick={() => { setAdding(false); setReplacing(null); setBody('') }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** The handover, after somebody has taken the call.
 *
 *  Section 02 step 6: everything held back from the broadcast is handed over
 *  AFTER acceptance, to the responder actually going. "A radio-only responder
 *  gets it over the air from the dispatcher."
 *
 *  Nobody has the card app yet, so the dispatcher IS the delivery mechanism —
 *  which means the one thing this has to do is put the words in front of him
 *  at the moment he keys the mic, rather than leaving him to remember that
 *  notes exist and go looking.
 */
export function Handover({ place, going }: { place: Place | null; going: string[] }) {
  if (!place) return null
  const notes = place.notes ?? []
  if (!notes.length && !place.entry_codes) return null

  return (
    <div className="handover">
      <div className="sn-title">
        Read this to {going.length ? going.join(' and ') : 'whoever is going'}
      </div>
      {place.entry_codes && (
        <div className="sn-note info">
          <span className="sev">Entry</span>
          <span className="body">{place.entry_codes}</span>
        </div>
      )}
      {/* THIS IS WORDS TO READ OUT, NOT A PLACE TO EDIT THEM. No Replace and
          no Retire here on purpose: the dispatcher is mid-handover with the
          mic in his hand, and the note list he can change is the one above the
          call. Nothing to gate — anything added here would need
          `add_site_note` and the `mine || edit_others_note` test that the
          working-call list carries. */}
      {notes.map((n) => (
        <div className={`sn-note ${n.severity}`} key={n.id}>
          <span className="sev">{SEVERITY[n.severity] ?? 'Note'}</span>
          <span className="body">{n.body}</span>
        </div>
      ))}
      <p className="footnote">
        Held back from the broadcast on purpose — it goes to whoever is
        actually going, not to everyone deciding whether to.
      </p>
    </div>
  )
}
